import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

import { ArrowLeft, Search, Download, Users, BarChart3, TrendingUp, Loader2, Send, Scale } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { collection, query, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { onAuthStateChanged } from "firebase/auth";
import StudentResultDetails from "../Teacherdashboard/StudentResultDetails";

interface StudentPerformanceViewProps {
  onBack: () => void;
}

interface ClassItem {
  id: string;
  name: string;
  classUid?: string;
}

interface Subject {
  id: string;
  name: string;
}

interface Term {
  id: string;
  name: string;
  isCurrent?: boolean;
  examTypes?: ExamType[];
}

interface Grade {
  studentId: string;
  subjectId: string;
  marks: number;
  normalizedExamScore?: number;
  examSessionId: string;
  examTypeId?: string;
  categoryId?: string;
}

interface ExamType {
  id: string;
  name: string;
  weight: number;
  status: "active" | "inactive";
}



interface Student {
  id: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  name: string; // Computed full name
  registrationNumber: string;
  admissionNumber: string; // Computed or alias
  classId: string;
  currentClassUid?: string; // Added for filtering
  photoUrl?: string; // Added
  grades?: Grade[];
  average?: number;
  rank?: number;
  [key: string]: any; // For dynamic subject scores
}

const StudentPerformanceView = ({ onBack }: StudentPerformanceViewProps) => {
  const { toast } = useToast();
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedSubject, setSelectedSubject] = useState("all");
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [selectedExamType, setSelectedExamType] = useState("all");


  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("rank");
  
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [terms, setTerms] = useState<Term[]>([]);
  const [examTypes, setExamTypes] = useState<ExamType[]>([]);

  
  const [loading, setLoading] = useState(true);
  const [schoolId, setSchoolId] = useState<string | null>(null);

  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);

  const currentWeight = useMemo(() => {
    if (selectedExamType !== 'all') {
      const type = examTypes.find(t => t.id === selectedExamType);
      return type?.weight ? `${type.weight}%` : 'N/A';
    }
    if (selectedTerm !== 'all') {
      return "100%";
    }
    return "N/A";
  }, [selectedTerm, selectedExamType, examTypes]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        const userSchoolId = idTokenResult.claims.schoolID as string;
        setSchoolId(userSchoolId);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!schoolId) return;

    const fetchSchoolData = async () => {
      try {
        setLoading(true);
        
        // Fetch Classes
        const classesQuery = query(collection(db, "schools", schoolId, "classes"));
        const classesSnapshot = await getDocs(classesQuery);
        const classesData = classesSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          classUid: doc.data().classUid 
        } as ClassItem));
        setClasses(classesData);

        // Fetch Subjects
        const subjectsQuery = query(collection(db, "schools", schoolId, "subjects"));
        const subjectsSnapshot = await getDocs(subjectsQuery);
        const subjectsData = subjectsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Subject));
        setSubjects(subjectsData);

        // Fetch Exam Sessions (Terms)
        const sessionsQuery = query(collection(db, `schools/${schoolId}/examSessions`));
        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessionsData = sessionsSnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().sessionName || 'Unknown Term',
          isCurrent: doc.data().isCurrent || doc.data().status === 'open',
          examTypes: doc.data().examTypes || []
        } as Term));
        setTerms(sessionsData);


        // Initial Selections


        const currentSession = sessionsData.find(s => s.isCurrent);
        if (currentSession) {
          setSelectedTerm(currentSession.id);
        } else if (sessionsData.length > 0) {
          setSelectedTerm(sessionsData[0].id);
        }
        
        // Initial Exam Type selection
        if (currentSession && (currentSession as any).examTypes && (currentSession as any).examTypes.length > 0) {
           // We do not set selectedExamType here as it depends on Term state effect updating 'examTypes'
           // However, let's allow the dependent effect to handle it.
        }

        setLoading(false);



        // Fetch Students
        const studentsQuery = query(collection(db, "schools", schoolId, "students"));
        const studentsSnapshot = await getDocs(studentsQuery);
        const studentsData = studentsSnapshot.docs.map(doc => {
          const data = doc.data();
          const firstName = data.firstName || "";
          const lastName = data.lastName || "";
          const middleName = data.middleName || "";
          const fullName = `${firstName} ${middleName} ${lastName}`.trim() || "Unknown Student";
          
          return { 
            id: doc.id, 
            ...data,
            firstName,
            lastName,
            middleName,
            name: fullName,
            registrationNumber: data.registrationNumber || "",
            admissionNumber: data.registrationNumber || data.admissionNumber || "N/A",
            currentClassUid: data.currentClassUid,
            photoUrl: data.photoUrl || undefined
          } as Student;
        });

        // Fetch Grades from the correct collection
        const gradesQuery = query(collection(db, "schools", schoolId, "grades"));
        const gradesSnapshot = await getDocs(gradesQuery);
        const gradesData = gradesSnapshot.docs.map(doc => ({
          ...doc.data(),
          marks: doc.data().marks || 0,
          normalizedExamScore: doc.data().normalizedExamScore
        } as Grade));

        // Note: Filtering by term happens in the render/useMemo logic now to allow dynamic switching
        // Ideally we would set raw data and filter later, but the current structure processes students immediately.
        // We will process all grades here into a map, or re-process when term changes.
        // For simplicity and performance with moderate data, we can store raw data and process in a separate effect or useMemo.
        // HOWEVER, to keep changes minimal, let's just store the raw `gradesData` and `studentsData` in refs or state 
        // and have a `useEffect` that updates `students` state when `selectedTerm` changes.
        
        // Actually, let's restructure slightly to allow dynamic filtering without re-fetching.
        // We'll store RAW fetched data in refs/state, and then `processedStudents` will be a useMemo dependency.
        
        setRawStudents(studentsData);
        setRawGrades(gradesData);
        setLoading(false); // Initial load complete

      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to load performance data",
          variant: "destructive",
        });
        setLoading(false);
      }
    };

    fetchSchoolData();
  }, [schoolId, toast]);

  // Update Exam Types when Selected Term Changes
  // Update Exam Types when Selected Term Changes
  // Update Exam Types when Selected Term Changes
  useEffect(() => {
    if (selectedTerm !== 'all') {
        const session = terms.find(t => t.id === selectedTerm);
        if (session && (session as any).examTypes && (session as any).examTypes.length > 0) {
            setExamTypes((session as any).examTypes);
        } else {
            setExamTypes([]);
        }
    } else {
        setExamTypes([]);
    }
    // Always default to 'all' types when term changes, user can then drill down
    setSelectedExamType("all");
  }, [selectedTerm, terms]);

  // New state for raw data to allow client-side filtering
  const [rawStudents, setRawStudents] = useState<Student[]>([]);
  const [rawGrades, setRawGrades] = useState<Grade[]>([]);

  // processedStudents recalculates whenever raw data or selectedTerm changes
  const students = useMemo(() => {
    if (loading) return [];

    return rawStudents.map(student => {
        // Filter grades by selected Term (Equation: Grade Session ID === Selected Term ID)
        // If 'all' is selected (edge case), maybe show all? Usually users want a specific term.
        const studentGrades = rawGrades.filter(g => 
            g.studentId === student.id && 
            (selectedTerm === 'all' || g.examSessionId === selectedTerm) &&
            (selectedExamType === 'all' || g.examTypeId === selectedExamType)
        );
        
        // Calculate average
        const subjectTotals = new Map<string, number>();
        studentGrades.forEach(g => {
            const score = g.normalizedExamScore !== undefined ? g.normalizedExamScore : (g.marks || 0);
            const current = subjectTotals.get(g.subjectId) || 0;
            subjectTotals.set(g.subjectId, current + score);
        });

        const subjectsList = Array.from(subjectTotals.values());
        const totalScore = subjectsList.reduce((acc, curr) => acc + curr, 0);
        const average = subjectsList.length > 0 ? totalScore / subjectsList.length : 0;

        const subjectScores: {[key: string]: number} = {};
        subjectTotals.forEach((score, subjectId) => {
            const subject = subjects.find(s => s.id === subjectId);
            if (subject) {
                subjectScores[subject.name.toLowerCase()] = score;
            }
        });

        return {
            ...student,
            ...subjectScores,
            grades: studentGrades,
            average: parseFloat(average.toFixed(1))
        };
    });
  }, [rawStudents, rawGrades, selectedTerm, selectedExamType, subjects, loading]);
    
    // Calculate global ranks based on the currently filtered view
    const rankedStudents = useMemo(() => {
        const sorted = [...students].sort((a, b) => (b.average || 0) - (a.average || 0));
        return students.map(student => ({
            ...student,
            rank: sorted.findIndex(s => s.id === student.id) + 1
        }));
    }, [students]);


  // Filtered view for the table (search, class filter, sort)
  const filteredStudents = useMemo(() => {
    let filtered = [...rankedStudents];

    // Filter by class
    if (selectedClass !== "all") {
      filtered = filtered.filter(student => 
        student.currentClassUid === selectedClass || student.classId === selectedClass
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(student =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.admissionNumber.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort students
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "average":
          return (b.average || 0) - (a.average || 0);
        case "rank":
        default:
          return (a.rank || 0) - (b.rank || 0);
      }
    });
  }, [rankedStudents, selectedClass, searchTerm, sortBy]);

  const getGradeColor = (average: number) => {
    if (average >= 90) return "text-green-600";
    if (average >= 80) return "text-blue-600";
    if (average >= 70) return "text-yellow-600";
    return "text-red-600";
  };


  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Student performance data is being exported..."
    });
  };
  
  const handlePublish = () => {
    toast({
        title: "Publish Grades",
        description: "Publish functionality coming soon.",
        variant: "default"
    });
  };

  const totalStudents = filteredStudents.length;
  const averageScore = filteredStudents.length > 0 
    ? (filteredStudents.reduce((sum, student) => sum + (student.average || 0), 0) / filteredStudents.length).toFixed(1)
    : 0;
  const topPerformers = filteredStudents.filter(s => (s.average || 0) >= 90).length;



  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (selectedStudent) {
    return (
        <StudentResultDetails
            student={{
                id: selectedStudent.id,
                name: selectedStudent.name,
                regNumber: selectedStudent.registrationNumber,
                photoUrl: selectedStudent.photoUrl
            }}
            session={{
                id: selectedTerm,
                name: terms.find(t => t.id === selectedTerm)?.name || 'Unknown Term'
            }}
            schoolID={schoolId || ''}
            onBack={() => setSelectedStudent(null)}
            position={selectedClass !== 'all' ? selectedStudent.rank : undefined}
            totalStudents={totalStudents}
            overallPosition={selectedClass === 'all' ? selectedStudent.rank : undefined}
            overallTotal={totalStudents}
            allowEditing={true}
            showAiActions={true}
            canEditPrincipalRemarks={true}
        />
    );
  }

  return (
    <div className="min-h-screen bg-muted/30 overflow-hidden">
      {/* Compact Header */}
      <div className="bg-background border-b px-3 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-lg font-bold">Student Performance</h1>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={handlePublish} className="h-8 px-3 gap-1">
              <Send className="h-3 w-3" />
              <span className="text-xs">Publish</span>
            </Button>
            <Button size="sm" onClick={handleExport} className="h-8 px-3 gap-1 bg-blue-600 text-white">
              <Download className="h-3 w-3" />
              <span className="text-xs">Export</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Compact Filters - Single Row */}
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={selectedTerm} onValueChange={setSelectedTerm}>
            <SelectTrigger className="w-28 h-8 text-xs bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-gray-100">
              <SelectValue placeholder="Term" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B]">
              {terms.map((term) => (
                <SelectItem key={term.id} value={term.id} className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">
                  {term.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Exam Type Dropdown */}
          <Select value={selectedExamType} onValueChange={setSelectedExamType} disabled={selectedTerm === 'all' || examTypes.length === 0}>
            <SelectTrigger className="w-28 h-8 text-xs bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-gray-100">
              <SelectValue placeholder="Exam Type" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B]">
              <SelectItem value="all" className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">All Types</SelectItem>
              {examTypes.map((type) => (
                <SelectItem key={type.id} value={type.id} className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>



          {/* Weight Display */}
          <Badge variant="secondary" className="flex items-center px-3 h-8 gap-1.5 whitespace-nowrap">
            <Scale className="h-3 w-3" />
            <span className="text-[10px] uppercase tracking-wider opacity-70 font-bold">Weight:</span>
            <span className="font-bold">{currentWeight}</span>
          </Badge>

          <Select value={selectedClass} onValueChange={setSelectedClass}>
            <SelectTrigger className="w-28 h-8 text-xs bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-gray-100">
              <SelectValue placeholder="Class" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B]">
              <SelectItem value="all" className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">All Classes</SelectItem>
              {classes.map((cls) => (
                <SelectItem key={cls.id} value={cls.classUid || cls.id} className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">
                  {cls.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedSubject} onValueChange={setSelectedSubject}>
            <SelectTrigger className="w-28 h-8 text-xs bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-gray-100">
              <SelectValue placeholder="Subject" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B]">
              <SelectItem value="all" className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">All Subjects</SelectItem>
              {subjects.map((subject) => (
                <SelectItem key={subject.id} value={subject.id} className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">
                  {subject.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-24 h-8 text-xs bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B] text-gray-900 dark:text-gray-100">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-[#020817] border-gray-200 dark:border-[#1E293B]">
              <SelectItem value="rank" className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">Rank</SelectItem>
              <SelectItem value="name" className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">Name</SelectItem>
              <SelectItem value="average" className="text-xs focus:bg-gray-100 dark:focus:bg-[#1E293B]">Average</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative flex-1 min-w-[120px] max-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>
        </div>

        {/* Compact Summary - Inline Stats */}
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-blue-500" />
            <span className="font-bold">{totalStudents}</span>
            <span className="text-muted-foreground text-xs">students</span>
          </div>
          <div className="flex items-center gap-1">
            <TrendingUp className="h-4 w-4 text-green-500" />
            <span className="font-bold">{averageScore}%</span>
            <span className="text-muted-foreground text-xs">avg</span>
          </div>
          <div className="flex items-center gap-1">
            <BarChart3 className="h-4 w-4 text-purple-500" />
            <span className="font-bold">{topPerformers}</span>
            <span className="text-muted-foreground text-xs">top</span>
          </div>
        </div>

              {/* Student Table */}
              <Card>
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-base">Student Performance Details</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div>
                    <table className="text-xs">
                      <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-800">
                        <tr>
                          <th className="px-2 py-1 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">#</th>
                          <th className="px-2 py-1 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Reg No</th>
                          <th className="px-2 py-1 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Name</th>
                          {subjects.map(subject => (
                            <th key={subject.id} className="px-2 py-1 text-center text-xs font-medium text-slate-500 dark:text-slate-400" title={subject.name}>
                              {subject.name.substring(0, 3)}
                            </th>
                          ))}
                          <th className="px-2 py-1 text-center text-xs font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Avg</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudents.map((student) => {
                          const avg = student.average || 0;
                          
                          return (
                            <tr 
                                key={student.id} 
                                className="hover:bg-blue-500/20 transition-colors cursor-pointer"
                                onDoubleClick={() => setSelectedStudent(student)}
                            >
                              <td className="px-2 py-1 text-center">{student.rank}</td>
                              <td className="px-2 py-1 text-[10px]">{student.admissionNumber}</td>
                              <td className="px-2 py-1">
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                                        {student.photoUrl ? (
                                            <img src={student.photoUrl} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-[10px] font-bold text-slate-500">
                                                {student.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                            </span>
                                        )}
                                    </div>
                                    <span className="truncate max-w-[120px]" title={student.name}>
                                        {student.name}
                                    </span>
                                </div>
                              </td>
                              {subjects.map(subject => {
                                const score = (student as any)[subject.name.toLowerCase()];
                                return (
                                  <td key={subject.id} className="px-2 py-1 text-center">
                                    {typeof score === 'number' ? Math.round(score) : '-'}
                                  </td>
                                );
                              })}
                              <td className={`px-2 py-1 text-center font-bold ${getGradeColor(avg)}`}>
                                {avg.toFixed(1)}%
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {filteredStudents.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">No students found matching your criteria.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
      </div>
    </div>
  );
};export default StudentPerformanceView;